import React, { useRef, useState, useMemo } from "react";
import * as htmlToImage from "html-to-image";
import QRCode from "react-qr-code";
import { X, Download, Share2, Fuel, MapPin, Route, Truck, Ship, CircleDollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, FUEL_TYPES } from "./constants";

export default function ShareCard({
  open,
  onClose,
  liveResult,
  distance,
  consumption,
  selectedFuelType,
  currentFuelPrice,
  returnTrip,
  tripName,
  originInput,
  destinationInput,
  selectedVehicle,
}) {
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  // Build share URL (used in QR code)
  const shareUrl = useMemo(() => {
    if (!liveResult) return "";
    const params = new URLSearchParams();
    params.set("share", "1");
    if (distance) params.set("d", distance);
    if (consumption) params.set("c", consumption);
    if (selectedFuelType) params.set("ft", selectedFuelType);
    if (currentFuelPrice) params.set("p", currentFuelPrice.toString());
    if (returnTrip) params.set("rt", "1");
    if (tripName) params.set("tn", tripName);
    if (originInput) params.set("or", originInput);
    if (destinationInput) params.set("de", destinationInput);
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }, [liveResult, distance, consumption, selectedFuelType, currentFuelPrice, returnTrip, tripName, originInput, destinationInput]);

  if (!open || !liveResult) return null;

  const fuelMeta = FUEL_TYPES[selectedFuelType] || FUEL_TYPES.diesel;
  const titleLabel = (tripName && tripName.trim()) || (originInput && destinationInput ? `${shorten(originInput)} → ${shorten(destinationInput)}` : "My Trip");

  function shorten(s) {
    if (!s) return "";
    return s.split(",")[0].trim().slice(0, 28);
  }

  const generatePng = async () => {
    if (!cardRef.current) return null;
    return await htmlToImage.toPng(cardRef.current, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: "#F7F5F0",
    });
  };

  const handleDownload = async () => {
    setExporting(true);
    try {
      const dataUrl = await generatePng();
      if (!dataUrl) return;
      const link = document.createElement("a");
      const safeName = (tripName || "drivespend-trip").replace(/[^a-z0-9]/gi, "-").toLowerCase();
      link.download = `${safeName}-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Trip card downloaded!");
    } catch (err) {
      console.error(err);
      toast.error("Could not export image. Try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    setExporting(true);
    try {
      const dataUrl = await generatePng();
      if (!dataUrl) return;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "drivespend-trip.png", { type: "image/png" });

      const shareText = `${titleLabel} — ${formatCurrency(liveResult.total_cost)} via DRIVE$PEND`;

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "DRIVE$PEND trip",
          text: shareText,
        });
        toast.success("Shared!");
      } else {
        // Fallback: copy text + download
        try {
          await navigator.clipboard.writeText(shareText);
          toast.success("Caption copied — image downloaded");
        } catch (err) {
          console.error("Clipboard write failed:", err);
        }
        const link = document.createElement("a");
        link.download = "drivespend-trip.png";
        link.href = dataUrl;
        link.click();
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error(err);
        toast.error("Sharing failed");
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="share-card-modal"
    >
      <div className="w-full max-w-[420px]" onClick={(e) => e.stopPropagation()}>
        {/* Top bar */}
        <div className="flex items-center justify-between mb-4 px-1">
          <div className="text-white text-sm font-bold tracking-[0.18em] uppercase">Trip Card</div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md text-white flex items-center justify-center transition-all"
            data-testid="share-card-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Exportable Card */}
        <div
          ref={cardRef}
          className="relative bg-[#F7F5F0] rounded-[28px] overflow-hidden shadow-2xl"
          style={{ width: "100%", aspectRatio: "4 / 5", fontFamily: "Manrope, sans-serif" }}
          data-testid="share-card-canvas"
        >
          {/* Decorative red gradient blob */}
          <div
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-60"
            style={{
              background: "radial-gradient(circle, rgba(229,32,32,0.18) 0%, rgba(229,32,32,0.05) 50%, transparent 75%)",
            }}
          />
          {/* Grid texture */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(0,0,0,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.6) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
          />

          {/* Content */}
          <div className="relative z-10 h-full flex flex-col p-7">
            {/* Brand row */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-[#1A1A1A] flex items-center justify-center">
                  <Fuel className="w-4 h-4 text-white" />
                </div>
                <span
                  className="text-[15px] font-bold tracking-tight text-[#1A1A1A]"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  DRIVE<span className="text-[#E52020]" style={{ fontFamily: "'IBM Plex Mono',monospace" }}>$</span>PEND
                </span>
              </div>
              <span className="text-[9px] font-bold tracking-[0.2em] text-[#999999] uppercase">Live · NZ</span>
            </div>

            {/* Trip title */}
            <div className="mb-1">
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#999999] mb-2">Trip</div>
              <h2
                className="text-[#1A1A1A] leading-[1.05] tracking-tighter"
                style={{
                  fontFamily: "Outfit, sans-serif",
                  fontWeight: 300,
                  fontSize: "30px",
                  wordBreak: "break-word",
                }}
              >
                {titleLabel}
              </h2>
            </div>

            {/* Origin / Destination */}
            {originInput && destinationInput && (
              <div className="mt-4 flex items-start gap-3">
                <div className="flex flex-col items-center pt-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#16A34A]" />
                  <span className="w-px h-7 bg-[#D4CEC0]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#E52020]" />
                </div>
                <div className="flex-1 min-w-0 text-[12px] text-[#666666]">
                  <div className="truncate">{shorten(originInput)}</div>
                  <div className="h-3" />
                  <div className="truncate">{shorten(destinationInput)}</div>
                </div>
              </div>
            )}

            {/* Total cost — hero */}
            <div className="flex-1 flex flex-col justify-end">
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#999999] mb-1">Estimated Cost</div>
              <div
                className="text-[#1A1A1A] leading-none tracking-[-0.04em]"
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontWeight: 500,
                  fontSize: "64px",
                }}
              >
                {formatCurrency(liveResult.total_cost)}
              </div>

              {/* Breakdown chips */}
              <div className="mt-5 grid grid-cols-2 gap-2">
                <Stat icon={<Route className="w-3 h-3" />} label="Distance" value={`${liveResult.distance_km} km${returnTrip ? " (rt)" : ""}`} />
                <Stat icon={<Fuel className="w-3 h-3" />} label={fuelMeta.label} value={`${formatCurrency(currentFuelPrice)}/L`} />
                <Stat icon={<Fuel className="w-3 h-3" />} label="Fuel cost" value={formatCurrency(liveResult.fuel_cost)} />
                {liveResult.ruc_cost > 0 && <Stat icon={<Truck className="w-3 h-3" />} label="RUC" value={formatCurrency(liveResult.ruc_cost)} />}
                {liveResult.toll_cost > 0 && <Stat icon={<CircleDollarSign className="w-3 h-3" />} label="Tolls" value={formatCurrency(liveResult.toll_cost)} />}
                {liveResult.ferry_cost > 0 && <Stat icon={<Ship className="w-3 h-3" />} label="Ferry" value={formatCurrency(liveResult.ferry_cost)} />}
              </div>

              {/* Footer with QR code */}
              <div className="mt-5 pt-4 border-t border-[#EBE6DC] flex items-end justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-[#999999] font-medium truncate">
                    {selectedVehicle?.name ? `${selectedVehicle.name} · ` : ""}{consumption} L/100km
                  </div>
                  <div className="mt-1 text-[10px] font-bold tracking-[0.18em] uppercase text-[#E52020]">drivespend.nz</div>
                  <div className="mt-0.5 text-[8px] font-bold tracking-[0.18em] uppercase text-[#999999]">Scan to open trip →</div>
                </div>
                {shareUrl && (
                  <div className="bg-white p-1.5 rounded-lg border border-[#EBE6DC] shrink-0">
                    <QRCode value={shareUrl} size={56} level="M" fgColor="#1A1A1A" bgColor="#FFFFFF" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            onClick={handleDownload}
            disabled={exporting}
            className="flex items-center justify-center gap-2 py-3.5 px-5 rounded-full bg-white text-[#1A1A1A] text-sm font-bold tracking-[0.08em] uppercase hover:bg-[#FAFAF7] transition-all disabled:opacity-50"
            data-testid="share-card-download-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download
          </button>
          <button
            onClick={handleShare}
            disabled={exporting}
            className="flex items-center justify-center gap-2 py-3.5 px-5 rounded-full bg-[#E52020] text-white text-sm font-bold tracking-[0.08em] uppercase hover:bg-[#CC1B1B] transition-all shadow-[0_8px_24px_rgba(229,32,32,0.34)] disabled:opacity-50"
            data-testid="share-card-share-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Share
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-white border border-[#EBE6DC] rounded-xl px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[9px] font-bold tracking-[0.16em] uppercase text-[#999999]">
        {icon}{label}
      </div>
      <div className="text-[15px] font-bold text-[#1A1A1A] mt-0.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{value}</div>
    </div>
  );
}
